/**
 * Which record scopes the Graph reads, and how a node names its scope.
 * (Graph with team items, 27.09.2026.)
 *
 * THE PERMISSION GATE IS THE TEAMS ROSTER, NOT SQLITE. Leaving a team does not
 * purge its `team:<id>` rows from this device, so a graph that enumerated the
 * scopes it finds in storage would keep showing a team the user has left. Team
 * scopes therefore come ONLY from `useTeamsStore.teams` — the same array
 * `boardsStore.teamBoardScopes()` reads — and they are taken through
 * `mcp/scopes.resolveScopes()`, the one reader every MCP list tool already
 * uses: same roster, same "signed in with no teams = team:* unavailable" rule,
 * and no new store importer inside the MCP import graph (mcpAttackSurface.test
 * pins the exact set of modules that read a store).
 *
 * Order is fixed and deterministic: personal → device → teams sorted by id.
 * `buildGraph` keeps the first node it sees for an id, so this order is also
 * the collision rule when one id exists in two scopes (e.g. a crash mid-move).
 *
 * Two vocabularies:
 *   scopeKey  the engine bucket: `personal:<userId>` | `personal:local` |
 *             `device:<userId>` | `team:<teamId>` — never leaves the app
 *   wire      what a node carries and MCP shows: `personal` | `team:<teamId>`
 *             (a device item is the user's own, so its wire scope is personal)
 *
 * This module is in the MCP import graph (graphSnapshot): keep it invoke-free.
 */
import { deviceScopeId, locationOfScope } from '../records/itemLocation';
import { personalScopeId } from '../services/personalScope';
import { resolveScopes } from '../mcp/scopes';

export const PERSONAL_WIRE = 'personal';

/** Engine scope key → the wire form a node carries. */
export function wireScopeOf(scopeKey) {
  const key = String(scopeKey || '');
  return key.startsWith('team:') ? key : PERSONAL_WIRE;
}

/** `team:<id>` → `<id>`; anything else → null. */
export function teamIdOfScope(scope) {
  const key = String(scope || '');
  return key.startsWith('team:') && key.length > 'team:'.length ? key.slice('team:'.length) : null;
}

/**
 * The scopes a graph build reads, in the fixed order, plus what could not be
 * known. Signed in with an empty roster → `unavailable: ['teams']`: there is
 * no "teams loaded" flag, so "no teams" and "not loaded yet" look the same and
 * the honest answer is to say so (mcp/scopes.js resolveScopes does the same).
 */
export function graphRecordScopes() {
  const personal = personalScopeId();
  const device = deviceScopeId();
  const roster = resolveScopes(null); // every reachable scope: personal + one per team on the roster
  const teams = roster.scopes
    .map(teamIdOfScope)
    .filter((id) => id != null)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const scopes = [
    { scopeKey: personal, wire: PERSONAL_WIRE, location: locationOfScope(personal), teamId: null },
    ...(device ? [{ scopeKey: device, wire: PERSONAL_WIRE, location: locationOfScope(device), teamId: null }] : []),
    ...[...new Set(teams)].map((id) => ({ scopeKey: `team:${id}`, wire: `team:${id}`, location: 'team', teamId: id })),
  ];
  const unavailable = roster.unavailable.includes('team:*') ? ['teams'] : [];
  return { scopes, unavailable };
}

/** Sorted wire scopes of the teams on the roster — a cache key for MCP. */
export function teamScopeSignature() {
  return graphRecordScopes()
    .scopes.filter((s) => s.teamId != null)
    .map((s) => s.wire)
    .join(',');
}
